import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { appBarHeight, navBarHeight } from '../../Constants';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import { useTitle } from 'shouldve-been-react';
import type Order from '../../Models/OrderModel';
import { datetimeToVisualString } from '../../Utils/DateTimeParser';
import { ErrorMessage, LoadDataBackdrop, LoadingButton } from 'shouldve-been-mui';
import AccessType from '../../Enums/AccessType';
import Button from '@mui/material/Button';
import { updateLastActivity } from '../../Utils/LastActivity';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import ApproveOrderDialog from '../../Dialogs/OrderDialogs/ApproveOrderDialog';
import type { NavigateFunction} from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import PostponeOrderDialog from '../../Dialogs/OrderDialogs/PostponeOrderDialog';
import AddRemarkDialog from '../../Dialogs/OrderDialogs/AddRemarkDialog';

type OrderInfoSubTabProps = {
    orderId: string,
    order: Order | null,
    closeSubTab: () => void,
    canApproveReadyOnly: boolean,
    canShowReqApproval: boolean,
    canPostpone: boolean,
    canRequestVesselToReapprove: boolean,
    setRerenderFlag: (value: Date) => void,
};

function OrderInfoSubTab(props: Readonly<OrderInfoSubTabProps>): JSX.Element {

    useTitle('K-Fleet Purchase - Order Info');

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);

    const currentTheme: Theme = useTheme();

    const navigate: NavigateFunction = useNavigate();

    const [orderHTML, setOrderHTML] = useState<string | null>(null);
    const [approveRequisitionPending, setApproveRequisitionPending] = useState<boolean>(false);
    const [requestVesselToReapprovePending, setRequestVesselToReapprovePending] = useState<boolean>(false);
    const [showApproveOrderDialog, setShowApproveOrderDialog] = useState<boolean>(false);
    const [showPostponeOrderDialog, setShowPostponeOrderDialog] = useState<boolean>(false);
    const [showAddRemarkDialog, setShowAddRemarkDialog] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [orderHTMLIsLoaded, setOrderHTMLIsLoaded] = useState<boolean>(false);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    const orderHTMLElement: any = useMemo<any>(() => {

        return {__html: orderHTML};
    }, [orderHTML]);

    const canReqApprove: boolean = useMemo<boolean>(() => {

        if (props.order === null) {
            return false;
        }

        if (!props.canShowReqApproval) {
            return false;
        }

        if (props.order.shipOrderedDate === null) {
            return false;
        }

        if (props.order.postponedToDate !== null && props.order.postponedToDate > new Date()) {
            return false;
        }

        if (props.order.requisitionApprovedDate !== null) {
            return false;
        }

        if (props.order.rfqSentDate !== null) {
            return false;
        }

        if (props.order.awaitingApproval !== null) {
            return false;
        }

        if (props.order.approvedDate !== null) {
            return false;
        }

        if (props.order.officeOrderedDate !== null) {
            return false;
        }

        if (props.order.confirmedDate !== null) {
            return false;
        }

        if (props.order.partlyDeliveredDate !== null) {
            return false;
        }

        if (props.order.onboardDate !== null) {
            return false;
        }

        if (props.order.finishedDate !== null) {
            return false;
        }

        if (!(userContext.currentUser?.access.includes(AccessType.PU_O_APPRQ) ?? true)) {
            return false;
        }

        return true;
    }, [props.canShowReqApproval, props.order, userContext.currentUser?.access]);

    const canOrderApprove: boolean = useMemo<boolean>(() => {

        if (props.order === null) {
            return false;
        }

        if (props.canApproveReadyOnly && props.order.awaitingApproval === null) {
            return false;
        }

        if (props.order.approvedDate !== null) {
            return false;
        }

        if (props.order.officeOrderedDate !== null) {
            return false;
        }

        if (props.order.finishedDate !== null) {
            return false;
        }

        if (props.order.postponedToDate !== null && props.order.postponedToDate > new Date()) {
            return false;
        }

        if (props.canShowReqApproval && props.order.requisitionApprovedDate === null) {
            return false;
        }

        if (!(userContext.currentUser?.access.includes(AccessType.PU_O_APP) ?? true)) {
            return false;
        }

        return true;
    }, [props.canApproveReadyOnly, props.canShowReqApproval, props.order, userContext.currentUser?.access]);

    const canPostpone: boolean = useMemo<boolean>(() => {

        if (!props.canPostpone) {
            return false;
        }

        if (!(userContext.currentUser?.access.includes(AccessType.PU_O_APP) ?? true)) {
            return false;
        }

        return true;
    }, [props.canPostpone, userContext.currentUser?.access]);

    const canRequestVesselToReapprove: boolean = useMemo<boolean>(() => {

        if (!props.canRequestVesselToReapprove) {
            return false;
        }

        if (!(userContext.currentUser?.access.includes(AccessType.PU_O_APP) ?? true)) {
            return false;
        }

        return true;
    }, [props.canRequestVesselToReapprove, userContext.currentUser?.access]);
    
    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        let mounted: boolean = true;

        async function fetchOrderHTML(): Promise<void> {

            setOrderHTMLIsLoaded(false);

            const newOrderHTML: string | null = await apiContext.api?.fetchOrderHTML(props.orderId, tokenContext.token) ?? null;

            if (!mounted) {
                return;
            }

            if (newOrderHTML !== null) {
                
                const modifiedOrderHTML: string = newOrderHTML.replace(/T\d{2}:\d{2}:\d{2}\.\d{3}Z/gu, '');

                setOrderHTML(modifiedOrderHTML);
            }
            
            setOrderHTMLIsLoaded(true);
        }

        void fetchOrderHTML();

        return () => {
            mounted = false;
        };
    }, [apiContext.api, props.orderId, tokenContext.token]);

    async function handleApproveRequisition(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        if (!canReqApprove || approveRequisitionPending) {
            return;
        }

        setApproveRequisitionPending(true);

        const success: boolean = await apiContext.api?.setStatusDate(props.orderId, 'reqapprove', null, tokenContext.token) ?? false;

        if (success) {
            props.setRerenderFlag(new Date());
            navigate('/orders');
        } else {
            setErrorMessage('Failed to requisition approve order');
        }

        setApproveRequisitionPending(false);
    }

    async function handleRequestVessselToReapprove(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        if (!canRequestVesselToReapprove || requestVesselToReapprovePending) {
            return;
        }

        setRequestVesselToReapprovePending(true);

        const success: boolean = await apiContext.api?.setStatusDate(props.orderId, 'requestvesseltoreapprove', null, tokenContext.token) ?? false;

        if (success) {
            props.setRerenderFlag(new Date());
            navigate('/orders');
        } else {
            setErrorMessage('Failed to request vessel to re-approve order');
        }

        setRequestVesselToReapprovePending(false);
    }

    function handleApproveOrder(): void {

        updateLastActivity(userContext, localStorageContext);

        if (!canOrderApprove) {
            return;
        }

        setShowApproveOrderDialog(true);
    }

    function handleCloseApproveOrderDialog(isApproved: boolean): void {

        updateLastActivity(userContext, localStorageContext);

        setShowApproveOrderDialog(false);

        if (isApproved) {

            props.setRerenderFlag(new Date());
            navigate('/orders');
        }
    }

    function handlePostponeOrder(): void {

        updateLastActivity(userContext, localStorageContext);

        if (!canPostpone) {
            return;
        }

        setShowPostponeOrderDialog(true);
    }

    function handleClosePostponeOrderDialog(isPostponed: boolean): void {

        updateLastActivity(userContext, localStorageContext);

        setShowPostponeOrderDialog(false);

        if (isPostponed) {

            props.setRerenderFlag(new Date());
            navigate('/orders');
        }
    }

    function handleAddRemark(): void {

        updateLastActivity(userContext, localStorageContext);

        setShowAddRemarkDialog(true);
    }

    function handleCloseAddRemarkDialog(isAdded: boolean): void {

        updateLastActivity(userContext, localStorageContext);

        setShowAddRemarkDialog(false);

        if (isAdded) {

            props.setRerenderFlag(new Date());
            navigate('/orders');
        }
    }

    return (
        <>
            <AppBar 
                position='sticky'
                elevation={0}
                style={{
                    background: currentTheme.palette.background.paper,
                    height: appBarHeight,
                }}
            >
                <Toolbar style={{
                    justifyContent: 'left',
                }}>
                    <IconButton 
                        edge='end' 
                        color='inherit' 
                        onClick={(): void => props.closeSubTab()}
                    >
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            color={currentTheme.palette.text.primary}    
                        />
                    </IconButton>
                    <Typography
                        noWrap
                        style={{
                            marginLeft: '30px',
                            color: currentTheme.palette.text.primary,
                            
                        }}
                    >
                        {(props.order?.vesselCode ?? '') + '_' + (props.order?.orderRef ?? '')}
                    </Typography>
                </Toolbar>
            </AppBar>
            <div style={{
                textAlign: 'center',
                padding: '25px 5px 5px 5px',
                height: windowSize[1] - navBarHeight - appBarHeight,
                overflow: 'auto',
                position: 'relative',
            }}>
                {props.order === null
                    ? (
                        <span style={{
                            color: currentTheme.palette.text.primary,
                        }}>
                            Could not show order
                        </span>
                    )
                    : (
                        <>
                            <span style={{
                                margin: '5px 10px',
                                display: canReqApprove ? 'inline-block' : 'none',
                            }}>
                                <LoadingButton 
                                    width='120px'
                                    height='32px'
                                    text='Approve Requisition'
                                    controller={approveRequisitionPending}
                                    onClick={handleApproveRequisition}
                                    buttonColor='primary'
                                    circularColor='error'
                                    circularSize='30px'
                                    variant='contained'
                                />
                            </span>
                            <span style={{
                                margin: '5px 10px',
                                display: canOrderApprove ? 'inline-block' : 'none',
                            }}>
                                <Button 
                                    onClick={handleApproveOrder}
                                    color='primary'
                                    variant='contained'
                                    style={{
                                        minWidth: '120px',
                                        maxWidth: '120px',
                                        minHeight: '32px',
                                        maxHeight: '32px',
                                    }}
                                >
                                    Approve
                                </Button>
                            </span>
                            <span style={{
                                margin: '5px 10px',
                                display: canPostpone ? 'inline-block' : 'none',
                            }}>
                                <Button 
                                    onClick={handlePostponeOrder}
                                    color='error'
                                    variant='contained'
                                    style={{
                                        minWidth: '120px',
                                        maxWidth: '120px',
                                        minHeight: '32px',
                                        maxHeight: '32px',
                                    }}
                                >
                                    Postpone
                                </Button>
                            </span>
                            <span style={{
                                margin: '5px 10px',
                                display: canRequestVesselToReapprove ? 'inline-block' : 'none',
                            }}>
                                <LoadingButton 
                                    width='120px'
                                    height='32px'
                                    text='Request Vessel to Reapprove'
                                    controller={requestVesselToReapprovePending}
                                    onClick={handleRequestVessselToReapprove}
                                    buttonColor='secondary'
                                    circularColor='error'
                                    circularSize='30px'
                                    variant='contained'
                                    fontSize='10px'
                                />
                            </span>
                            <span style={{
                                margin: '5px 10px',
                                display: 'inline-block',
                            }}>
                                <Button 
                                    onClick={handleAddRemark}
                                    color='primary'
                                    variant='contained'
                                    style={{
                                        minWidth: '120px',
                                        maxWidth: '120px',
                                        minHeight: '32px',
                                        maxHeight: '32px',
                                        lineHeight: 'normal',
                                    }}
                                >
                                    Add Remark
                                </Button>
                            </span>
                            <ErrorMessage
                                message={errorMessage}
                                maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                            />
                            <table style={{
                                color: currentTheme.palette.text.primary,
                                marginTop: '20px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                fontSize: '20px',
                            }}>
                                <tbody>
                                    <tr>
                                        <td style={{
                                            textAlign: 'right',
                                            padding: '5px 10px',
                                            fontWeight: 'bold',
                                        }}>
                                            Order NO:
                                        </td>
                                        <td style={{
                                            textAlign: 'left',
                                            padding: '5px 10px',
                                        }}>
                                            {props.order.orderRef}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{
                                            textAlign: 'right',
                                            padding: '5px 10px',
                                            fontWeight: 'bold',
                                        }}>
                                            Vessel Name:
                                        </td>
                                        <td style={{
                                            textAlign: 'left',
                                            padding: '5px 10px',
                                        }}>
                                            {props.order.vesselName}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{
                                            textAlign: 'right',
                                            padding: '5px 10px',
                                            fontWeight: 'bold',
                                        }}>
                                            Subject:
                                        </td>
                                        <td style={{
                                            textAlign: 'left',
                                            padding: '5px 10px',
                                        }}>
                                            {props.order.subject}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{
                                            textAlign: 'right',
                                            padding: '5px 10px',
                                            fontWeight: 'bold',
                                        }}>
                                            Received Office Date:
                                        </td>
                                        <td style={{
                                            textAlign: 'left',
                                            padding: '5px 10px',
                                        }}>
                                            {datetimeToVisualString(props.order.receivedOfficeDate, true, 0)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{
                                            textAlign: 'right',
                                            padding: '5px 10px',
                                            fontWeight: 'bold',
                                        }}>
                                            Inquiry Date:
                                        </td>
                                        <td style={{
                                            textAlign: 'left',
                                            padding: '5px 10px',
                                        }}>
                                            {datetimeToVisualString(props.order.rfqSentDate, true, 0)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{
                                            textAlign: 'right',
                                            padding: '5px 10px',
                                            fontWeight: 'bold',
                                        }}>
                                            Awaiting Approval Date:
                                        </td>
                                        <td style={{
                                            textAlign: 'left',
                                            padding: '5px 10px',
                                        }}>
                                            {datetimeToVisualString(props.order.awaitingApproval, true, 0)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style={{
                                position: 'relative',
                                padding: '10px',
                                minHeight: '150px',
                            }}>
                                <div 
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                    dangerouslySetInnerHTML={orderHTMLElement}
                                    style={{
                                        textAlign: 'center',
                                    }}
                                />

                                <LoadDataBackdrop
                                    dataIsLoaded={orderHTMLIsLoaded}
                                    zIndex={1500}
                                />
                            </div>

                            <ApproveOrderDialog
                                controller={showApproveOrderDialog}
                                onClose={handleCloseApproveOrderDialog}
                                order={props.order}
                            />

                            <PostponeOrderDialog
                                controller={showPostponeOrderDialog}
                                onClose={handleClosePostponeOrderDialog}
                                order={props.order}
                            />

                            <AddRemarkDialog
                                controller={showAddRemarkDialog}
                                onClose={handleCloseAddRemarkDialog}
                                order={props.order}
                            />
                        </>
                    )
                }
            </div>
        </>
    );
}

export default OrderInfoSubTab;
